import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { endOfToday, format, startOfToday, subDays } from 'date-fns';

import { useDescriptionContentUtils } from '@ecp/common/src/utils/useDescriptionContentUtils';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import { CalendarRange } from '@ecp/common/src/components/calendar/bo/Calendar';
import CheckboxGroupDropdown from '@ecp/common/src/components/checkboxGroupDropdown/CheckboxGroupDropdown';
import { metaDescriptionContent, documentTitle } from '@ecp/common/src/const/htmlHeadMessage';

import { userInfoState } from '@bo-recoil/common/user/atom';
import { useCoDashboardGridOptions } from '@bo-hooks/dashBoard/useCoDashboard';
import { useAlertDialog } from '@bo-hooks/common/useModal';
import { getCoNotificationList } from '@bo-apis/co/customerService/coNotificationApi';
import coMemberApi from '@bo-apis/co/member/coMemberApi';
import DashboardContentBox from '@bo-components/dashBoard/DashboardContentBox';
import Grid from '@bo-components/common/grid/Grid';
import CoNoticePopup from '@bo-components/dashBoard/CoNoticePopup';
import CoMainSummary from '@bo-components/dashBoard/CoMainSummary';
import { ReactComponent as RefreshIcon } from '@bo-assets/icon/common/icon__refresh.svg';
import WelmallLogo from '@bo-assets/image/common/image__ci_logo.png';

const CoMainPage = () => {
  // const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);
  const [showNoticePopup, setShowNoticePopup] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState('');
  const [memberSummaryList, setMemberSummaryList] = useState([...initMemberSummaryList]);
  const [orderSummaryList, setOrderSummaryList] = useState([...initOrderSummaryList]);
  // 회원 소속 dropdown ( 계열사 )
  const [sbComList, setSbComList] = useState([]);
  const { showAlertDialog } = useAlertDialog();

  // 기간 CalendarRange ( 현재날짜-29일 ~ 현재 날짜 )
  const [searchDate] = useState({
    fromDate: subDays(startOfToday(), 29),
    toDate: endOfToday(),
  });

  /*PO html head meta description content*/
  useDescriptionContentUtils(metaDescriptionContent.coContent);
  useLayoutEffect(() => {
    document.title = documentTitle.coTitle;
  }, []);

  const { gridOptions } = useCoDashboardGridOptions();

  const getRefreshSummary = useCallback(async (sbComList) => {
      // 체크된 계열사
      const checkedSbComList = sbComList.filter(({ checked }) => checked).map(({ value }) => value);
      const searchForm = {
        fromDate: format(searchDate.fromDate, 'yyyyMMddHHmmss'),
        toDate: format(searchDate.toDate, 'yyyyMMddHHmmss'),
        sbcomCodeList: checkedSbComList,
      };

      const {
        result: {
          totalMemberCount,
          regularMemberCount,
          associateMemberCount,
          orderCount,
          noOrderCount,
          personalPayCount,
        },
      } = await coMemberApi.getMemberCount({ searchForm });

      setMemberSummaryList((prev) => {
        const tmpMemberSummary = [...prev];
        tmpMemberSummary[1].cnt = totalMemberCount;
        tmpMemberSummary[2].cnt = regularMemberCount;
        tmpMemberSummary[3].cnt = associateMemberCount;
        return tmpMemberSummary;
      });

      setOrderSummaryList((prev) => {
        const tmpOrderSummary = [...prev];
        tmpOrderSummary[1].cnt = orderCount;
        tmpOrderSummary[2].cnt = noOrderCount;
        tmpOrderSummary[3].cnt = personalPayCount;
        return tmpOrderSummary;
      });
    },
    [searchDate]
  );

  const getSbComList = useCallback(async () => {
    // 계열사 목록 조회
    try {
      const { result } = await coMemberApi.getSbComList({ searchForm: {} });
      setSbComList(
        result.map(({ sbComCd, sbComNm }) => ({
          label: sbComNm,
          value: sbComCd,
          checked: true,
        }))
      );
    } catch (e) {
      console.error('error', e);
    }
  }, []);

  useEffect(() => {
    (async () => await getSbComList())();
  }, [getSbComList]);

  useEffect(() => {
    if (sbComList.length > 0) {
      (async () => await getRefreshSummary(sbComList))();
    }
  }, [getRefreshSummary, sbComList]);

  const handleSearch = useCallback(
    async (pageIndex, pageSize, sort = {}) => {
      try {
        const params = {
          fromDateTime: format(startOfToday(), 'yyyyMMddHHmmss'),
          toDateTime: format(endOfToday(), 'yyyyMMddHHmmss'),
          title: '',
        };
        const {
          result: { notificationList, counts },
        } = await getCoNotificationList({
          searchForm: params,
          pageable: { size: pageSize, page: pageIndex, sort },
        });
        setContent(notificationList.content);
        setTotal(counts);
      } catch (e) {
        showAlertDialog('입력 값이 올바르지 않습니다.');
      }
    },
    [setContent, setTotal]
  );

  const handleRowClick = useCallback(
    ({ row }) => {
      if (row?.Kind !== 'Header') {
        setShowNoticePopup(true);
        setSelectedNotice(row.notificationNumber.toString());
      }
    },
    [setShowNoticePopup]
  );

  // 계열사 dropDown 확인 버튼 click event handler
  const handleChangeSbComConfirm = useCallback((e, sbComList) => {
    setSbComList(sbComList);
  }, []);

  return (
    <>
      <Container>
        <img src={WelmallLogo} alt={'logo'} />
        <Spacing bottom={12} />
        <TextBox font-weight={'700'} font-size={24}>
          {userInfo?.siteName}
        </TextBox>
        <Spacing bottom={60} />
        <SearchContainer gap={'20px'}>
          <FlexBox gap={'6px'}>
            <TextBox>회원소속</TextBox>
            <CheckboxGroupDropdown itemList={sbComList} onChange={handleChangeSbComConfirm} width={'164px'} />
          </FlexBox>
          <FlexBox gap={'6px'}>
            <TextBox>기간</TextBox>
            <CalendarRange
              readOnly
              startDate={searchDate.fromDate}
              endDate={searchDate.toDate}
              inputStyle={{ width: '106px' }}
            />
            <RefreshButton onClick={() => getRefreshSummary(sbComList)}>
              <RefreshIcon />
            </RefreshButton>
          </FlexBox>
        </SearchContainer>
        <Spacing bottom={14} />
        <FlexBox gap={'16px'}>
          <CoMainSummary list={memberSummaryList} />

          <CoMainSummary list={orderSummaryList} />
        </FlexBox>
        <Spacing bottom={20} />
        <FlexBox>
          <DashboardContentBox title={'공지사항'} width={'1100px'} bodyHeight={'450px'}>
            <Grid
              unPaged
              readOnly
              total={total}
              data={content}
              options={gridOptions}
              onSearch={handleSearch}
              onClick={handleRowClick}
              height={'300px'}
            />
          </DashboardContentBox>
        </FlexBox>
      </Container>
      <CoNoticePopup
        isCO={true}
        open={showNoticePopup}
        onClose={() => setShowNoticePopup(false)}
        noticeSeq={selectedNotice}
      />
    </>
  );
};

export default CoMainPage;

const Container = styled(FlexBoxColumn)`
  padding: 44px 30px 0;
  align-items: flex-start;
`;

const RefreshButton = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

const SearchContainer = styled(FlexBox)`
  width: 100%;
  justify-content: end;
`;

const initMemberSummaryList = [
  { label: '누적 회원 수', title: true },
  { label: '총 회원 수', cnt: 0, unit: '명' },
  { label: '정회원 수', cnt: 0, unit: '명' },
  { label: '준회원 수', cnt: 0, unit: '명' },
];

const initOrderSummaryList = [
  { label: '주문자 수(중복제외)', title: true },
  { label: '주문자 수', cnt: 0, unit: '명' },
  { label: '미주문자 수(누적)', cnt: 0, unit: '명' },
  { label: '개인결제 주문자 수', cnt: 0, unit: '명' },
];
